import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "src/components/base/dialog";
import { Button } from "../base/button";
import { ApiInternal } from "src/api/api_internal";
import { SimpleGroup } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link DeleteGroupDialog}
 */
export type DeleteGroupDialogProps = {
    /** Callback for closing the popup */
    onClose: () => void;

    /** Callback for successful deletion */
    onDeletion: () => void;

    /** The group */
    group: SimpleGroup;
};

/**
 * A dialog to delete a group
 */
export default function DeleteGroupDialog(props: DeleteGroupDialogProps) {
    const [t] = useTranslation("dialog-delete-group");
    const [tg] = useTranslation();

    const [errors, setErrors] = React.useState<Array<string>>([]);

    /**
     * Delete a group
     */
    const deleteGroup = async () => {
        const res = await ApiInternal.groups.local.delete(props.group.uuid);
        if (res.result === "Err") {
            const errors = [];
            if (res.error.access_rules) {
                errors.push(t("error.access-rules-with-group", { count: res.error.access_rules }));
            }
            setErrors(errors);

            return;
        }
        toast.success(t("toast.group-deleted-successfully"));
        props.onDeletion();
    };

    return (
        <Dialog open={true} onClose={props.onClose}>
            <DialogTitle>{t("heading.delete-group", { group: props.group.name })}</DialogTitle>
            <DialogDescription>{t("description.delete-group")}</DialogDescription>
            {errors.length > 0 && (
                <div className={"mt-3 flex flex-col gap-1 text-sm text-red-500 dark:text-red-700"}>
                    {errors.map((err, idx) => (
                        <span key={idx}>{err}</span>
                    ))}
                </div>
            )}
            <DialogActions>
                <Button onClick={props.onClose}>{tg("button.cancel")}</Button>
                <Button color={"red"} onClick={async () => await deleteGroup()}>
                    {t("button.delete-group")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
